import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import html2pdf from 'html2pdf.js';
import backendApi from '../shared/api/backendApi';
import { userManagementApi } from '../shared/api/userApi';
import { getStoredInvoicePrintProfile } from '../shared/printProfile';

// Helper function to generate customer statement HTML
const generateCustomerStatementHTML = (statementData) => {
  const { customerName, customerPhone, totalAmount, totalPaid, outstandingDue, creditBalance, invoiceCount, paymentCount, payments, invoices, generatedAt, businessData } = statementData;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <title>Customer Statement - ${customerName}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Inter', sans-serif;
          line-height: 1.6;
          color: #1f2937;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 20px;
          min-height: 100vh;
        }
        .container {
          max-width: 1200px;
          margin: 0 auto;
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        .header {
          position: relative;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%);
          color: white;
          padding: 40px 30px;
          text-align: center;
        }
        .header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 30% 40%, rgba(255,255,255,0.1) 0%, transparent 50%);
        }
        .header-content {
          position: relative;
          z-index: 1;
        }
        .company-name {
          font-size: 32px;
          font-weight: 900;
          margin-bottom: 8px;
          text-shadow: 0 4px 8px rgba(0,0,0,0.3);
          background: linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .company-details {
          font-size: 16px;
          opacity: 0.9;
          margin-bottom: 20px;
        }
        .statement-title {
          font-size: 28px;
          font-weight: 800;
          margin: 20px 0;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        .customer-info {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 30px;
          border-left: 6px solid #667eea;
          margin: 30px;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        .customer-info h3 {
          color: #667eea;
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 15px;
        }
        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 25px;
          padding: 0 30px;
          margin-bottom: 40px;
        }
        .summary-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 25px;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
          position: relative;
          overflow: hidden;
        }
        .summary-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%);
        }
        .summary-card.total {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        .summary-card.paid {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
        }
        .summary-card.due {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
        }
        .summary-card.invoices {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3);
        }
        .summary-card.payments {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          box-shadow: 0 8px 25px rgba(245, 158, 11, 0.3);
        }
        .summary-card.credit {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          box-shadow: 0 8px 25px rgba(5, 150, 105, 0.3);
        }
        .card-content {
          position: relative;
          z-index: 1;
        }
        .card-title {
          font-size: 14px;
          opacity: 0.9;
          margin-bottom: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .card-value {
          font-size: 32px;
          font-weight: 900;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        .invoice-section {
          padding: 0 30px 40px;
        }
        .section-title {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .section-title::before {
          content: '📋';
          font-size: 28px;
        }
        .invoice-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
          border-radius: 12px;
          overflow: hidden;
        }
        .invoice-table th,
        .invoice-table td {
          padding: 16px 20px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        .invoice-table th {
          background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
          color: white;
          font-weight: 700;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .invoice-table tr:nth-child(even) {
          background: linear-gradient(90deg, rgba(243, 244, 246, 0.5) 0%, rgba(249, 250, 251, 0.5) 100%);
        }
        .invoice-table tr:hover {
          background: linear-gradient(90deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%);
          transform: translateY(-1px);
          transition: all 0.2s ease;
        }
        .status-paid {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          color: #166534;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          border: 1px solid #86efac;
        }
        .status-due {
          background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
          color: #dc2626;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          border: 1px solid #fca5a5;
        }
        .footer {
          background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
          color: white;
          padding: 30px;
          text-align: center;
          position: relative;
        }
        .footer::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%);
        }
        .footer-content {
          position: relative;
          z-index: 1;
        }
        .amount {
          font-weight: 700;
          text-align: right;
          font-size: 16px;
        }
        .total-row {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
          font-weight: 800;
          font-size: 16px;
          border-top: 2px solid #667eea;
        }
        .total-row td {
          color: #1f2937;
        }
        @media print {
          body {
            padding: 0;
            background: white;
          }
          .container {
            box-shadow: none;
            border-radius: 0;
          }
          .no-print {
            display: none !important;
          }
        }
        @media (max-width: 768px) {
          .summary-cards {
            grid-template-columns: 1fr;
          }
          .header, .customer-info, .invoice-section {
            padding-left: 20px;
            padding-right: 20px;
          }
          .company-name {
            font-size: 24px;
          }
          .statement-title {
            font-size: 20px;
          }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div class="header-content">
            <div class="company-name">${businessData.companyName || 'Your Business Name'}</div>
            <div class="company-details">
              ${businessData.address || ''}<br>
              ${businessData.phone ? `📞 ${businessData.phone}` : ''} ${businessData.email ? `✉️ ${businessData.email}` : ''}
            </div>
            <div class="statement-title">CUSTOMER STATEMENT</div>
          </div>
        </div>

        <div class="customer-info">
          <h3>👤 Customer Details</h3>
          <strong style="font-size: 18px; color: #1f2937;">${customerName}</strong><br>
          <span style="color: #6b7280;">📱 ${customerPhone || 'N/A'}</span><br>
          <span style="color: #6b7280;">📅 Statement Date: ${generatedAt}</span>
        </div>

        <div class="summary-cards">
          <div class="summary-card total">
            <div class="card-content">
              <div class="card-title">💰 Total Amount</div>
              <div class="card-value">₹${totalAmount.toLocaleString('en-IN')}</div>
            </div>
          </div>
          <div class="summary-card paid">
            <div class="card-content">
              <div class="card-title">✅ Total Paid</div>
              <div class="card-value">₹${totalPaid.toLocaleString('en-IN')}</div>
            </div>
          </div>
          <div class="summary-card due">
            <div class="card-content">
              <div class="card-title">⏳ Outstanding Due</div>
              <div class="card-value">₹${outstandingDue.toLocaleString('en-IN')}</div>
            </div>
          </div>
          <div class="summary-card credit">
            <div class="card-content">
              <div class="card-title">💎 Credit Balance</div>
              <div class="card-value">₹${creditBalance.toLocaleString('en-IN')}</div>
            </div>
          </div>
          <div class="summary-card invoices">
            <div class="card-content">
              <div class="card-title">📄 Total Invoices</div>
              <div class="card-value">${invoiceCount}</div>
            </div>
          </div>
          <div class="summary-card payments">
            <div class="card-content">
              <div class="card-title">💳 Total Payments</div>
              <div class="card-value">${paymentCount}</div>
            </div>
          </div>
        </div>

        <div class="invoice-section">
          <div class="section-title">Invoice Details</div>

          <table class="invoice-table">
            <thead>
              <tr>
                <th>🆔 Invoice ID</th>
                <th>📅 Date</th>
                <th>📦 Items</th>
                <th class="amount">💰 Amount</th>
                <th class="amount">💳 Paid</th>
                <th class="amount">⏳ Due</th>
                <th>📊 Status</th>
              </tr>
            </thead>
            <tbody>
              ${invoices.map(invoice => `
                <tr>
                  <td style="font-weight: 700; color: #667eea;">${invoice.invoiceId}</td>
                  <td>${invoice.date}</td>
                  <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis;">${invoice.items}</td>
                  <td class="amount">₹${invoice.amount.toLocaleString('en-IN')}</td>
                  <td class="amount" style="color: #10b981; font-weight: 600;">₹${invoice.paid.toLocaleString('en-IN')}</td>
                  <td class="amount" style="color: ${invoice.due > 0 ? '#ef4444' : '#10b981'}; font-weight: 600;">₹${invoice.due.toLocaleString('en-IN')}</td>
                  <td><span class="status-${invoice.status.toLowerCase()}">${invoice.status}</span></td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3" style="text-align: right; font-weight: 900; font-size: 18px;">📊 TOTALS:</td>
                <td class="amount" style="font-size: 18px; color: #667eea;">₹${totalAmount.toLocaleString('en-IN')}</td>
                <td class="amount" style="font-size: 18px; color: #10b981;">₹${totalPaid.toLocaleString('en-IN')}</td>
                <td class="amount" style="font-size: 18px; color: ${outstandingDue > 0 ? '#ef4444' : '#10b981'};">₹${outstandingDue.toLocaleString('en-IN')}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        ${payments && payments.length > 0 ? `
        <div class="invoice-section">
          <div class="section-title">Payment Records</div>

          <table class="invoice-table">
            <thead>
              <tr>
                <th>📅 Date</th>
                <th>💰 Amount</th>
                <th>💳 Mode</th>
                <th>📋 Notes</th>
                <th>🔗 Allocated To</th>
              </tr>
            </thead>
            <tbody>
              ${payments.map(payment => `
                <tr>
                  <td>${payment.date}</td>
                  <td class="amount" style="color: #10b981; font-weight: 600;">₹${payment.amount.toLocaleString('en-IN')}</td>
                  <td>${payment.mode}</td>
                  <td style="max-width: 200px;">${payment.notes || 'N/A'}</td>
                  <td style="max-width: 250px;">
                    ${payment.allocations && payment.allocations.length > 0
                      ? payment.allocations.map(alloc =>
                          alloc.invoice_no ? `${alloc.invoice_no}: ₹${parseFloat(alloc.allocated_amount).toLocaleString('en-IN')}` : 'Credit'
                        ).join(', ')
                      : 'N/A'
                    }
                  </td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="4" style="text-align: right; font-weight: 900; font-size: 18px;">💰 TOTAL PAYMENTS:</td>
                <td class="amount" style="font-size: 18px; color: #10b981;">₹${payments.reduce((sum, p) => sum + p.amount, 0).toLocaleString('en-IN')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        ` : ''}

        <div class="footer">
          <div class="footer-content">
            <p style="font-size: 18px; font-weight: 700; margin-bottom: 10px;">🙏 Thank you for your business!</p>
            <p style="opacity: 0.9; margin-bottom: 15px;">This statement includes all transactions up to ${generatedAt}.</p>
            <p style="opacity: 0.8;">For any questions, please contact us at ${businessData.phone || businessData.email || 'your contact information'}</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

const createCustomerEditForm = (customer = {}) => ({
  name: customer.name || '',
  phone: customer.phone || customer.mobile || '',
  address: customer.address || '',
  city: customer.city || '',
  state: customer.state || '',
  zip_code: customer.zip_code || customer.zipCode || '',
  country: customer.country || ''
});

const createPaymentForm = () => ({
  amount: '',
  paymentDate: new Date().toISOString().split('T')[0],
  paymentMode: 'cash',
  notes: ''
});

const mapBusinessData = (businessInfo = {}) => ({
  companyName: businessInfo.name || businessInfo.companyName || 'Your Business Name',
  name: businessInfo.name || businessInfo.companyName || 'Your Business Name',
  address: businessInfo.address || 'Your Business Address',
  phone: businessInfo.phone || businessInfo.businessPhone || 'Your Phone',
  businessPhone: businessInfo.phone || businessInfo.businessPhone || 'Your Phone',
  email: businessInfo.email || 'your@email.com',
  gstNumber: businessInfo.gstNumber || businessInfo.gst_number || '',
  gst: businessInfo.gstNumber || businessInfo.gst_number || '',
  businessType: businessInfo.businessType || businessInfo.type || businessInfo.business_type || '',
  type: businessInfo.type || businessInfo.businessType || businessInfo.business_type || '',
  industry: businessInfo.industry || '',
  website: businessInfo.website || '',
  city: businessInfo.city || '',
  state: businessInfo.state || '',
  zipCode: businessInfo.zipCode || businessInfo.zip_code || '',
  country: businessInfo.country || 'India',
  description: businessInfo.description || '',
  employeeCount: businessInfo.employeeCount || businessInfo.employee_count || '',
  foundedYear: businessInfo.foundedYear || businessInfo.founded_year || '',
  logoUrl: '',
  terms: 'Payment due within 30 days'
});

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSummaryOpen, setCustomerSummaryOpen] = useState(false);
  const [customerPayments, setCustomerPayments] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(createPaymentForm());
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerEditForm, setCustomerEditForm] = useState(createCustomerEditForm());
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [businessData, setBusinessData] = useState({
    companyName: 'Your Business Name',
    address: 'Your Business Address',
    phone: 'Your Phone',
    email: 'your@email.com',
    gstNumber: '',
    logoUrl: '',
    terms: 'Payment due within 30 days'
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only load customers from the database API
      const customersResponse = await backendApi.getCustomers();

      // Database API returns { success: true, customers: [...], totalCustomers: number, generatedAt: string }
      const customersData = customersResponse.customers || [];

      setCustomers(customersData);
      return customersData;
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBusinessData = useCallback(async () => {
    try {
      const localUserData = localStorage.getItem('userData');
      if (!localUserData) {
        console.log('No user data found in localStorage');
        return;
      }

      const parsedUserData = JSON.parse(localUserData);
      const businessStorageKey = 'business_' + parsedUserData.id;
      const cachedBusiness = localStorage.getItem(businessStorageKey);
      let businessInfo = null;

      if (cachedBusiness) {
        businessInfo = JSON.parse(cachedBusiness);
        console.log('Business data loaded from localStorage:', businessInfo);
      } else {
        console.log('No business data in localStorage, fetching from API...');
        try {
          const businessResponse = await userManagementApi.getBusinessProfile(parsedUserData.id);
          if (businessResponse?.success && businessResponse.business) {
            businessInfo = businessResponse.business;
            localStorage.setItem(businessStorageKey, JSON.stringify(businessInfo));
            localStorage.setItem('userData', JSON.stringify({
              ...parsedUserData,
              business: businessInfo
            }));
            console.log('Business data loaded from API and cached');
          }
        } catch (apiError) {
          console.error('Failed to fetch business data from API:', apiError);
        }
      }

      if (!businessInfo) {
        console.log('No business data found in localStorage or API');
        return;
      }

      const realBusinessData = mapBusinessData(businessInfo);
      console.log('Real business data loaded:', realBusinessData);
      setBusinessData(realBusinessData);
    } catch (err) {
      console.error('Error loading business data:', err);
      console.log('Using sample business data as fallback');
    }
  }, []);

  useEffect(() => {
    loadData();
    loadBusinessData();
  }, [loadBusinessData, loadData]);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => customers.filter((customer) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    const name = (customer.name || '').toLowerCase();
    const mobile = (customer.phone || customer.mobile || '').toLowerCase();

    return name.includes(searchLower) ||
           mobile.includes(searchLower) ||
           String(customer.total_amount || '').includes(searchLower) ||
           String(customer.total_paid || '').includes(searchLower) ||
           String(customer.total_due || '').includes(searchLower) ||
           String(customer.credit_balance || '').includes(searchLower) ||
           String(customer.total_orders || '').includes(searchLower);
  }), [customers, searchTerm]);

  const totalOutstandingDue = useMemo(
    () => filteredCustomers.reduce((sum, customer) => {
      const due = parseFloat(customer.total_due || 0);
      return sum + Math.max(0, due);
    }, 0),
    [filteredCustomers]
  );

  const totalPaymentsRecorded = useMemo(
    () => customerPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    [customerPayments]
  );

  const totalOutstandingInvoiceDue = useMemo(
    () => outstandingInvoices.reduce((sum, inv) => sum + parseFloat(inv['Due Amount'] || 0), 0),
    [outstandingInvoices]
  );

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }),
    []
  );

  const customerRows = useMemo(
    () => filteredCustomers.map((customer, index) => {
      const totalAmount = parseFloat(customer.total_amount || 0);
      const totalPaid = parseFloat(customer.total_paid || 0);
      const totalDue = parseFloat(customer.total_due || 0);
      const creditBalance = parseFloat(customer.credit_balance || 0);

      return {
        customer,
        index,
        key: customer.id || index,
        rowBackground: index % 2 === 0 ? 'white' : '#f8fafc',
        totalAmountFormatted: currencyFormatter.format(totalAmount),
        totalPaidFormatted: currencyFormatter.format(totalPaid),
        totalDueFormatted: currencyFormatter.format(totalDue),
        totalDueColor: totalDue > 0 ? 'error.main' : (totalDue < 0 ? 'success.main' : 'text.primary'),
        creditBalanceFormatted: currencyFormatter.format(creditBalance),
        creditBalanceColor: creditBalance > 0 ? 'success.main' : (creditBalance < 0 ? 'warning.main' : 'text.primary')
      };
    }),
    [currencyFormatter, filteredCustomers]
  );

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const handleCustomerClick = useCallback(async (customer) => {
    setSelectedCustomer(customer);
    setCustomerSummaryOpen(true);

    // Fetch customer payments and invoices from API
    try {
      const [paymentsResponse, invoicesResponse] = await Promise.all([
        backendApi.getCustomerPayments(customer.id),
        backendApi.getCustomerInvoices(customer.id)
      ]);

      setCustomerPayments(paymentsResponse.payments || []);
      
      // Get invoices from API response
      const invoices = invoicesResponse.invoices || [];
      setCustomerInvoices(invoices);

      // Get outstanding invoices for this customer (for auto-allocation display)
      const outstanding = invoices.filter(invoice =>
        parseFloat(invoice['Due Amount'] || invoice.due_amount || 0) > 0
      );
      setOutstandingInvoices(outstanding);

    } catch (error) {
      console.error('Error fetching customer data:', error);
      setCustomerPayments([]);
      setCustomerInvoices([]);
      setOutstandingInvoices([]);
    }
  }, []);

  const handleCloseCustomerSummary = useCallback(() => {
    setCustomerSummaryOpen(false);
    setSelectedCustomer(null);
    setCustomerPayments([]);
    setCustomerInvoices([]);
    setOutstandingInvoices([]);
    setEditDialogOpen(false);
    setSavingCustomer(false);
    setCustomerEditForm(createCustomerEditForm());
    setPaymentDialogOpen(false);
    setPaymentForm(createPaymentForm());
  }, []);

  const handleRecordPayment = useCallback(() => {
    setPaymentDialogOpen(true);
  }, []);

  const handlePaymentFormChange = useCallback((field, value) => {
    setPaymentForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleOpenEditCustomer = useCallback(() => {
    if (!selectedCustomer) return;
    setCustomerEditForm(createCustomerEditForm(selectedCustomer));
    setEditDialogOpen(true);
  }, [selectedCustomer]);

  const handleCloseEditCustomer = useCallback(() => {
    setEditDialogOpen(false);
    setSavingCustomer(false);
    setCustomerEditForm(createCustomerEditForm(selectedCustomer || {}));
  }, [selectedCustomer]);

  const handleCustomerEditChange = useCallback((field, value) => {
    setCustomerEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleUpdateCustomer = async () => {
    if (!selectedCustomer?.id) return;

    if (!customerEditForm.name.trim()) {
      alert('Customer name is required.');
      return;
    }

    try {
      setSavingCustomer(true);

      const payload = {
        name: customerEditForm.name.trim(),
        phone: (customerEditForm.phone || '').trim(),
        address: (customerEditForm.address || '').trim(),
        city: (customerEditForm.city || '').trim(),
        state: (customerEditForm.state || '').trim(),
        zip_code: (customerEditForm.zip_code || '').trim(),
        country: (customerEditForm.country || '').trim()
      };

      await backendApi.updateCustomer(selectedCustomer.id, payload);

      const refreshedCustomers = await loadData();
      const refreshedCustomer = refreshedCustomers.find(
        customer => String(customer.id) === String(selectedCustomer.id)
      );

      if (refreshedCustomer) {
        await handleCustomerClick(refreshedCustomer);
      } else {
        setSelectedCustomer(prev =>
          prev ? { ...prev, ...payload } : prev
        );
      }

      setEditDialogOpen(false);
      alert('Customer details updated successfully.');
    } catch (error) {
      console.error('Error updating customer details:', error);
      alert('Failed to update customer details. Please try again.');
    } finally {
      setSavingCustomer(false);
    }
  };


  const handleSubmitPayment = async () => {
    try {
      const paymentData = {
        paymentDate: paymentForm.paymentDate,
        amount: parseFloat(paymentForm.amount),
        paymentMode: paymentForm.paymentMode,
        notes: paymentForm.notes
      };

      await backendApi.recordCustomerPayment(selectedCustomer.id, paymentData);

      // Refresh customer data
      await loadData();
      if (selectedCustomer) {
        await handleCustomerClick(selectedCustomer);
      }

      setPaymentDialogOpen(false);
      setPaymentForm(createPaymentForm());

      alert('Payment recorded and automatically allocated to outstanding invoices (oldest first)!');
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Failed to record payment. Please try again.');
    }
  };

  // Get customer's sales/invoices (now from API state)
  const getCustomerInvoices = useCallback(() => customerInvoices, [customerInvoices]);

  const handleCustomerInvoiceFileAction = async (invoice, action = 'download') => {
    try {
      const invoiceId = invoice?.id;
      if (!invoiceId) {
        alert('Invoice ID not found for this record.');
        return;
      }

      let blob;
      try {
        // Fast path: use already generated PDF directly.
        blob = await backendApi.downloadInvoicePDF(invoiceId);
      } catch (downloadError) {
        // If missing, generate once and retry download.
        const status = downloadError?.response?.status;
        if (status !== 404) {
          throw downloadError;
        }
        const selectedTemplate = localStorage.getItem('selectedInvoiceTemplate') || 'simple';
        const printProfile = getStoredInvoicePrintProfile();
        await backendApi.generateInvoicePDF(invoiceId, selectedTemplate, printProfile);
        blob = await backendApi.downloadInvoicePDF(invoiceId);
      }

      const blobUrl = window.URL.createObjectURL(blob);

      if (action === 'print') {
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => printWindow.print();
        }
      } else {
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = `Invoice-${invoice['Invoice No'] || invoice.invoice_no || invoiceId}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1500);
    } catch (err) {
      console.error('Error processing customer invoice file action:', err);
      alert(action === 'print' ? 'Failed to print invoice. Please try again.' : 'Failed to download invoice. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}
      {/* Header section with title and total due card side by side */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        {/* Left side - Title, description, and search */}
        <Box sx={{ flex: 1, mr: 3 }}>
          <Typography variant="h4" gutterBottom>
            Customer Management
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Customer data is automatically updated from sales transactions.
          </Typography>

          {/* Search Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder="Search customers by name, mobile, invoices, or amounts..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              sx={{ flex: 1, maxWidth: 500 }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                ),
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
            {searchTerm && (
              <Typography variant="body2" color="text.secondary">
                Found {filteredCustomers.length} of {customers.length} customers
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right side - Total Due Card */}
        <Box sx={{ flexShrink: 0, width: 280 }}>
          <Card sx={{
            background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 25px rgba(220, 38, 38, 0.3)',
            height: 'fit-content'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Total Outstanding Due
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {'\u20B9'}{currencyFormatter.format(totalOutstandingDue)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                From all customers
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <TableContainer 
        component={Paper} 
        sx={{ 
          borderRadius: 2, 
          boxShadow: 2, 
          maxHeight: '80vh',
          overflow: 'auto',
          '& .MuiTableCell-root': {
            overflow: 'visible !important',
            textOverflow: 'clip !important',
            whiteSpace: 'normal !important'
          }
        }}
      >
        <Table
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-stickyHeader': {
              backgroundColor: '#1e293b',
              color: '#ffffff',
            },
            '& .MuiTableCell-head': {
              backgroundColor: '#1e293b',
              color: '#ffffff',
            }
          }}
        >
          <TableHead sx={{ backgroundColor: '#1e293b' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '20%', maxWidth: '20%', padding: '8px 6px' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '12%', maxWidth: '12%', padding: '8px 6px' }}>Mobile</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '10%', maxWidth: '10%', padding: '8px 6px' }}>Invoices</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '14%', maxWidth: '14%', padding: '8px 6px' }}>Total</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '14%', maxWidth: '14%', padding: '8px 6px' }}>Paid</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '14%', maxWidth: '14%', padding: '8px 6px' }}>Due</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '16%', maxWidth: '16%', padding: '8px 6px' }}>Credit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customerRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? 'No customers found matching your search.' : 'No customers yet. Add your first sale to create customers automatically.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customerRows.map(({ customer, index, key, rowBackground, totalAmountFormatted, totalPaidFormatted, totalDueFormatted, totalDueColor, creditBalanceFormatted, creditBalanceColor }) => (
                <TableRow
                  key={key}
                  onClick={() => handleCustomerClick(customer)}
                  sx={{
                    backgroundColor: rowBackground,
                    '&:hover': {
                      backgroundColor: '#e2e8f0',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <TableCell sx={{ padding: '8px 6px', width: '20%', maxWidth: '20%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                      {customer.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '8px 6px', width: '12%', maxWidth: '12%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                      {customer.phone || customer.mobile || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '8px 6px', width: '10%', maxWidth: '10%' }}>
                    {customer.total_orders || 0} orders
                  </TableCell>
                  <TableCell sx={{ padding: '8px 6px', width: '14%', maxWidth: '14%' }}>{'\u20B9'}{totalAmountFormatted}</TableCell>
                  <TableCell sx={{ padding: '8px 6px', width: '14%', maxWidth: '14%' }}>{'\u20B9'}{totalPaidFormatted}</TableCell>
                  <TableCell sx={{ padding: '8px 6px', width: '14%', maxWidth: '14%' }}>
                    <Typography sx={{ fontWeight: 800, color: totalDueColor }}>
                      {'\u20B9'}{totalDueFormatted}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ padding: '8px 6px', width: '16%', maxWidth: '16%' }}>
                    <Typography sx={{ fontWeight: 800, color: creditBalanceColor }}>
                      {'\u20B9'}{creditBalanceFormatted}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Customer Summary Dialog */}
      <Dialog
        open={customerSummaryOpen}
        onClose={handleCloseCustomerSummary}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Customer Summary: {selectedCustomer?.name}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleOpenEditCustomer}
              >
                Edit Details
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<ReceiptIcon />}
                onClick={handleRecordPayment}
              >
                Record Payment
              </Button>
              <Button
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={async () => {
                  try {
                    const customerInvoices = getCustomerInvoices();
                    if (customerInvoices.length === 0) {
                      alert('No invoices found for this customer');
                      return;
                    }

                    // Generate comprehensive customer statement
                    const statementData = {
                      customerName: selectedCustomer.name,
                      customerPhone: selectedCustomer.phone || selectedCustomer.mobile,
                      totalAmount: parseFloat(selectedCustomer.total_amount || 0),
                      totalPaid: parseFloat(selectedCustomer.total_paid || 0),
                      outstandingDue: parseFloat(selectedCustomer.total_due || 0),
                      creditBalance: parseFloat(selectedCustomer.credit_balance || 0),
                      invoiceCount: customerInvoices.length,
                      paymentCount: customerPayments.length,
                      payments: customerPayments.map(payment => ({
                        date: new Date(payment.payment_date).toLocaleDateString('en-IN'),
                        amount: parseFloat(payment.amount),
                        mode: payment.payment_mode || 'N/A',
                        notes: payment.notes || '',
                        allocations: payment.allocations || []
                      })),
                      invoices: customerInvoices.map((invoice) => ({
                        invoiceId: invoice['Invoice No'] || invoice.invoice_no || `INV-${invoice.id}`,
                        date: invoice['Date'] || invoice.invoice_date,
                        items: invoice.items ? invoice.items.map(item => item.item_name).join(', ') : 'N/A',
                        amount: parseFloat(invoice['Final Amount'] || invoice['Total Amount'] || invoice.total_amount || 0),
                        paid: parseFloat(invoice['Total Paid'] || invoice.total_paid || 0),
                        due: parseFloat(invoice['Due Amount'] || invoice.due_amount || 0),
                        status: parseFloat(invoice['Due Amount'] || invoice.due_amount || 0) > 0 ? 'Due' : 'Paid'
                      })),
                      generatedAt: new Date().toLocaleDateString('en-IN'),
                      businessData: businessData
                    };

                    // Generate HTML for customer statement
                    const statementHtml = generateCustomerStatementHTML(statementData);

                    // Generate PDF using backend
                    const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store';
                    // For customer statements, we'll use html2pdf.js as it's not an invoice
                    const opt = {
                      margin: 0.5,
                      filename: `Customer-Statement-${selectedCustomer.name.replace(/\s+/g, '-')}.pdf`,
                      image: { type: 'jpeg', quality: 0.95 },
                      html2canvas: { scale: 2, useCORS: true },
                      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
                    };
                    await html2pdf().set(opt).from(statementHtml).save();
                    return; // Skip the rest since we're using html2pdf.js

                    if (renderResp.ok) {
                      const blob = await renderResp.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Customer-Statement-${selectedCustomer.name.replace(/\s+/g, '-')}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } else {
                      // Fallback to html2pdf.js if backend fails
                      console.warn('Backend PDF generation failed, using html2pdf.js fallback');
                      const opt = {
                        margin: 0.5,
                        filename: `Customer-Statement-${selectedCustomer.name.replace(/\s+/g, '-')}.pdf`,
                        image: { type: 'jpeg', quality: 0.95 },
                        html2canvas: { scale: 2, useCORS: true },
                        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
                      };
                      await html2pdf().set(opt).from(statementHtml).save();
                    }
                  } catch (error) {
                    console.error('Error generating customer statement PDF:', error);
                    alert('Failed to generate PDF. Please try again.');
                  }
                }}
              >
                Download Statement
              </Button>
              <Button
                size="small"
                startIcon={<PrintIcon />}
                onClick={async () => {
                  try {
                    const customerInvoices = getCustomerInvoices();
                    if (customerInvoices.length === 0) {
                      alert('No invoices found for this customer');
                      return;
                    }

                    // Generate comprehensive customer statement
                    const statementData = {
                      customerName: selectedCustomer.name,
                      customerPhone: selectedCustomer.phone || selectedCustomer.mobile,
                      totalAmount: parseFloat(selectedCustomer.total_amount || 0),
                      totalPaid: parseFloat(selectedCustomer.total_paid || 0),
                      outstandingDue: parseFloat(selectedCustomer.total_due || 0),
                      creditBalance: parseFloat(selectedCustomer.credit_balance || 0),
                      invoiceCount: customerInvoices.length,
                      paymentCount: customerPayments.length,
                      payments: customerPayments.map(payment => ({
                        date: new Date(payment.payment_date).toLocaleDateString('en-IN'),
                        amount: parseFloat(payment.amount),
                        mode: payment.payment_mode || 'N/A',
                        notes: payment.notes || '',
                        allocations: payment.allocations || []
                      })),
                      invoices: customerInvoices.map((invoice) => ({
                        invoiceId: invoice['Invoice No'] || invoice.invoice_no || `INV-${invoice.id}`,
                        date: invoice['Date'] || invoice.invoice_date,
                        items: invoice.items ? invoice.items.map(item => item.item_name).join(', ') : 'N/A',
                        amount: parseFloat(invoice['Final Amount'] || invoice['Total Amount'] || invoice.total_amount || 0),
                        paid: parseFloat(invoice['Total Paid'] || invoice.total_paid || 0),
                        due: parseFloat(invoice['Due Amount'] || invoice.due_amount || 0),
                        status: parseFloat(invoice['Due Amount'] || invoice.due_amount || 0) > 0 ? 'Due' : 'Paid'
                      })),
                      generatedAt: new Date().toLocaleDateString('en-IN'),
                      businessData: businessData
                    };

                    // Generate HTML for customer statement
                    const statementHtml = generateCustomerStatementHTML(statementData);

                    // Print using browser
                    const htmlDoc = `<!doctype html><html><head><meta charset="utf-8" />
                      <meta name="viewport" content="width=device-width, initial-scale=1" />
                      <style>@page{size:A4;margin:0} html,body{margin:0;padding:0;background:#fff} *{-webkit-print-color-adjust:exact; print-color-adjust:exact}</style>
                    </head><body>${statementHtml}
                      <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},50)}<\/script>
                    </body></html>`;
                    const printWin = window.open('', '_blank');
                    if (printWin) {
                      printWin.document.open();
                      printWin.document.write(htmlDoc);
                      printWin.document.close();
                    } else {
                      // Fallback to html2pdf.js for printing if popup blocked
                      console.warn('Print popup blocked, using html2pdf.js fallback');
                      const opt = {
                        margin: 0.5,
                        filename: `Customer-Statement-${selectedCustomer.name.replace(/\s+/g, '-')}.pdf`,
                        image: { type: 'jpeg', quality: 0.95 },
                        html2canvas: { scale: 2, useCORS: true },
                        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
                      };
                      const pdfBlob = await html2pdf().set(opt).from(statementHtml).outputPdf('blob');
                      const pdfUrl = URL.createObjectURL(pdfBlob);
                      const printWindow = window.open(pdfUrl, '_blank');
                      if (printWindow) {
                        printWindow.onload = () => printWindow.print();
                      }
                    }
                  } catch (error) {
                    console.error('Error printing customer statement:', error);
                    alert('Failed to print. Please try again.');
                  }
                }}
              >
                Print Statement
              </Button>
              <Button
                size="small"
                color="success"
                startIcon={<WhatsAppIcon />}
                onClick={async () => {
                  try {
                    const customerId = Number.parseInt(selectedCustomer?.id, 10);
                    if (!Number.isInteger(customerId) || customerId <= 0) {
                      setError("Valid customer ID is required to send summary.");
                      setTimeout(() => setError(""), 4000);
                      return;
                    }

                    const customerInvoices = getCustomerInvoices();
                    const statementData = {
                      customerName: selectedCustomer.name,
                      customerPhone: selectedCustomer.phone || selectedCustomer.mobile,
                      totalAmount: parseFloat(selectedCustomer.total_amount || 0),
                      totalPaid: parseFloat(selectedCustomer.total_paid || 0),
                      outstandingDue: parseFloat(selectedCustomer.total_due || 0),
                      creditBalance: parseFloat(selectedCustomer.credit_balance || 0),
                      invoiceCount: customerInvoices.length,
                      paymentCount: customerPayments.length,
                      payments: customerPayments.map(payment => ({
                        date: new Date(payment.payment_date).toLocaleDateString('en-IN'),
                        amount: parseFloat(payment.amount),
                        mode: payment.payment_mode || 'N/A',
                        notes: payment.notes || '',
                        allocations: payment.allocations || []
                      })),
                      invoices: customerInvoices.map((invoice) => ({
                        invoiceId: invoice['Invoice No'] || invoice.invoice_no || `INV-${invoice.id}`,
                        date: invoice['Date'] || invoice.invoice_date,
                        items: invoice.items ? invoice.items.map(item => item.item_name).join(', ') : 'N/A',
                        amount: parseFloat(invoice['Final Amount'] || invoice['Total Amount'] || invoice.total_amount || 0),
                        paid: parseFloat(invoice['Total Paid'] || invoice.total_paid || 0),
                        due: parseFloat(invoice['Due Amount'] || invoice.due_amount || 0),
                        status: parseFloat(invoice['Due Amount'] || invoice.due_amount || 0) > 0 ? 'Due' : 'Paid'
                      })),
                      generatedAt: new Date().toLocaleDateString('en-IN'),
                      businessData: businessData
                    };
                    const statementHtml = generateCustomerStatementHTML(statementData);

                    await backendApi.sendCustomerSummaryPdf(customerId, {
                      caption: `Customer Statement - ${selectedCustomer?.name || customerId}`,
                      footer: businessData?.companyName || businessData?.name || "Dhandhapaani",
                      htmlTemplate: statementHtml,
                      full: 1
                    });

                    setSuccess("Customer summary PDF sent on WhatsApp successfully.");
                    setTimeout(() => setSuccess(""), 3000);
                  } catch (apiError) {
                    const apiMessage = apiError?.response?.data?.message || apiError?.message || "Failed to send customer summary PDF on WhatsApp.";
                    setError(apiMessage);
                    setTimeout(() => setError(""), 5000);
                  }
                }}
              >
                WhatsApp PDF
              </Button>
              <Typography variant="body2" color="text.secondary">
                📞 {selectedCustomer?.phone || selectedCustomer?.mobile}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <Box>
              {/* Customer Overview */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 2.4 }}>
                  <Card sx={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">Total Amount</Typography>
                      <Typography variant="h4">₹{parseFloat(selectedCustomer.total_amount || 0).toLocaleString('en-IN')}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 2.4 }}>
                  <Card sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">Total Paid</Typography>
                      <Typography variant="h4">₹{parseFloat(selectedCustomer.total_paid || 0).toLocaleString('en-IN')}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 2.4 }}>
                  <Card sx={{
                    background: parseFloat(selectedCustomer.total_due || 0) > 0
                      ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                      : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    color: 'white'
                  }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">Outstanding Due</Typography>
                      <Typography variant="h4">₹{parseFloat(selectedCustomer.total_due || 0).toLocaleString('en-IN')}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 2.4 }}>
                  <Card sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">Total Orders</Typography>
                      <Typography variant="h4">{selectedCustomer.total_orders || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 2.4 }}>
                  <Card sx={{
                    background: parseFloat(selectedCustomer.credit_balance || 0) > 0
                      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                      : parseFloat(selectedCustomer.credit_balance || 0) < 0
                      ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                      : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                    color: 'white'
                  }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">Credit Balance</Typography>
                      <Typography variant="h4">₹{parseFloat(selectedCustomer.credit_balance || 0).toLocaleString('en-IN')}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Payment Records Section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Payment Records
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {customerPayments.length === 0 ? (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No payment records found for this customer.
                </Typography>
              ) : (
                <Box>
                  {customerPayments.map((payment, index) => (
                    <Card key={payment.id} sx={{ mb: 2, borderRadius: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" color="success.main">
                              ₹{parseFloat(payment.amount).toLocaleString('en-IN')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(payment.payment_date).toLocaleDateString('en-IN')}
                            </Typography>
                            {payment.payment_mode && (
                              <Chip label={payment.payment_mode} size="small" sx={{ mt: 1 }} />
                            )}
                            {payment.allocations && payment.allocations.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Allocated to:
                                </Typography>
                                {payment.allocations.map((alloc, idx) => (
                                  <Chip
                                    key={idx}
                                    label={`${alloc.invoice_no || `INV${alloc.invoice_id}`}: ₹${parseFloat(alloc.allocated_amount).toLocaleString('en-IN')}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: idx > 0 ? 1 : 0, mt: 0.5 }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                          {payment.notes && (
                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                              {payment.notes}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Payment Summary */}
                  <Card sx={{ mt: 2, backgroundColor: '#f8fafc' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Payment Summary
                      </Typography>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Total Payments Recorded:</Typography>
                        <Typography variant="h6" color="success.main">
                          {'\u20B9'}{currencyFormatter.format(totalPaymentsRecorded)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Invoice History */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Invoice History
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {getCustomerInvoices().length === 0 ? (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No invoices found for this customer.
                </Typography>
              ) : (
                <Box>
                  {getCustomerInvoices().map((invoice, index) => (
                    <Card key={invoice.id || index} sx={{ mb: 2, borderRadius: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Box>
                            <Typography variant="h6">
                              Invoice {invoice['Invoice No'] || invoice.invoice_no || `INV-${invoice.id}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Date: {invoice['Date'] || invoice.invoice_date}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="h6" color="primary">
                              ₹{parseFloat(invoice['Final Amount'] || invoice['Total Amount'] || invoice.total_amount || 0).toLocaleString('en-IN')}
                            </Typography>
                            <Chip
                              label={parseFloat(invoice['Due Amount'] || invoice.due_amount || 0) > 0 ? 'Due' : 'Paid'}
                              color={parseFloat(invoice['Due Amount'] || invoice.due_amount || 0) > 0 ? 'error' : 'success'}
                              size="small"
                            />
                          </Box>
                        </Box>

                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Items:</strong> {invoice.items ? invoice.items.map(item => item.item_name).join(', ') : 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          <strong>Payment:</strong> ₹{parseFloat(invoice['Total Paid'] || invoice.total_paid || 0).toLocaleString('en-IN')} paid,
                          ₹{parseFloat(invoice['Due Amount'] || invoice.due_amount || 0).toLocaleString('en-IN')} due
                        </Typography>

                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => handleCustomerInvoiceFileAction(invoice, 'download')}
                          >
                            Download PDF
                          </Button>
                          <Button
                            size="small"
                            startIcon={<PrintIcon />}
                            onClick={() => handleCustomerInvoiceFileAction(invoice, 'print')}
                          >
                            Print
                          </Button>
                          {(invoice['Phone Number'] || selectedCustomer.phone || selectedCustomer.mobile) && (
                            <Button
                              size="small"
                              startIcon={<WhatsAppIcon />}
                              color="success"
                              onClick={async () => {
                                // Use customer's phone if invoice doesn't have it
                                const phoneValue = invoice['Phone Number'] || selectedCustomer.phone || selectedCustomer.mobile;
                                const phone = (phoneValue && typeof phoneValue === 'string')
                                  ? phoneValue.replace(/\D/g, '')
                                  : (phoneValue ? String(phoneValue).replace(/\D/g, '') : '');
                                const invoiceId = invoice['Invoice No'] || invoice.invoice_no || `INV${(index + 1).toString().padStart(3, '0')}`;
                                const parsedFromInvoiceNo = Number.parseInt(String(invoiceId).replace(/\D/g, ''), 10);
                                const numericInvoiceId =
                                  invoice.id ||
                                  invoice.invoice_id ||
                                  invoice.sale_id ||
                                  (Number.isInteger(parsedFromInvoiceNo) && parsedFromInvoiceNo > 0 ? parsedFromInvoiceNo : null);
                                if (numericInvoiceId) {
                                  try {
                                    await backendApi.sendInvoicePdfToCustomer(numericInvoiceId, {
                                      caption: `Invoice ${invoiceId}`,
                                      footer: businessData?.companyName || businessData?.name || 'Dhandhapaani',
                                      full: 1
                                    });
                                    setSuccess('Invoice PDF sent on WhatsApp successfully.');
                                    setTimeout(() => setSuccess(''), 3000);
                                    return;
                                  } catch (apiError) {
                                    console.warn('WhatsApp invoice PDF send failed:', apiError);
                                    const apiMessage = apiError?.response?.data?.message || apiError?.message || 'Failed to send invoice PDF on WhatsApp.';
                                    setError(apiMessage);
                                    setTimeout(() => setError(''), 5000);
                                    return;
                                  }
                                }
                                setError('Invoice ID not found for this row, cannot send PDF.');
                                setTimeout(() => setError(''), 4000);
                                return;
                              }}
                            >
                              WhatsApp
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      <DialogActions>
          <Button onClick={handleCloseCustomerSummary}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Customer Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditCustomer}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Customer Details - {selectedCustomer?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={customerEditForm.name}
                  onChange={(e) => handleCustomerEditChange('name', e.target.value)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={customerEditForm.phone}
                  onChange={(e) => handleCustomerEditChange('phone', e.target.value)}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={customerEditForm.address}
                  onChange={(e) => handleCustomerEditChange('address', e.target.value)}
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={customerEditForm.city}
                  onChange={(e) => handleCustomerEditChange('city', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="State"
                  value={customerEditForm.state}
                  onChange={(e) => handleCustomerEditChange('state', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Zip / Pincode"
                  value={customerEditForm.zip_code}
                  onChange={(e) => handleCustomerEditChange('zip_code', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Country"
                  value={customerEditForm.country}
                  onChange={(e) => handleCustomerEditChange('country', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditCustomer} disabled={savingCustomer}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateCustomer}
            variant="contained"
            disabled={savingCustomer || !customerEditForm.name.trim()}
          >
            {savingCustomer ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Recording Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Record Payment - {selectedCustomer?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Payment Amount"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => handlePaymentFormChange('amount', e.target.value)}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Payment Date"
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => handlePaymentFormChange('paymentDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Payment Mode"
                  value={paymentForm.paymentMode}
                  onChange={(e) => handlePaymentFormChange('paymentMode', e.target.value)}
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="online">Online Transfer</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => handlePaymentFormChange('notes', e.target.value)}
                  placeholder="Optional payment notes"
                />
              </Grid>
            </Grid>

            {outstandingInvoices.length > 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom color="info.contrastText">
                  🤖 Automatic Allocation
                </Typography>
                <Typography variant="body2" color="info.contrastText">
                  This payment will be automatically allocated to outstanding invoices in chronological order (oldest first).
                  Found {outstandingInvoices.length} outstanding invoice(s) totaling {'\u20B9'}{currencyFormatter.format(totalOutstandingInvoiceDue)}.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitPayment}
            variant="contained"
            color="success"
            disabled={!paymentForm.amount || parseFloat(paymentForm.amount) <= 0}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Customers;




